import React, { useMemo, useState } from "react";

import { ChevronDown } from "lucide-react";

import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { StatusHistoryRecord } from "~/pages/purchase-orders/hooks/useStatusHistory";
import {
  getStatusDotColor,
  getStatusLabel,
} from "~/pages/purchase-orders/utils/statusLabels";
import { formatDate } from "~/utils/helpers";

interface StatusHistoryTimelineProps {
  history: StatusHistoryRecord[];
  loading: boolean;
  usersById: Record<string, { name: string; lastname: string; email: string }>;
}

// How many of the latest events to show before collapsing the rest.
const VISIBLE_COUNT = 3;

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};

// The hook falls back to the raw slug when the DB has no translation
// (display_name === new_status), so route that case through the local map.
const resolveStatusLabel = (record: StatusHistoryRecord): string => {
  if (record.display_name && record.display_name !== record.new_status) {
    return record.display_name;
  }
  return getStatusLabel(record.new_status);
};

export const StatusHistoryTimeline: React.FC<StatusHistoryTimelineProps> = ({
  history,
  loading,
  usersById,
}) => {
  const [expanded, setExpanded] = useState(false);

  // Newest first: the reader wants the latest change at the top of the list.
  const rows = useMemo(() => [...(history ?? [])].reverse(), [history]);

  const hiddenCount = Math.max(rows.length - VISIBLE_COUNT, 0);
  const alwaysRows = rows.slice(0, VISIBLE_COUNT);
  const tailRows = rows.slice(VISIBLE_COUNT);

  const getUserDisplayName = (changedBy: string | null) => {
    if (!changedBy || changedBy === "system") return "Sistema";
    const user = usersById[changedBy];
    if (!user) return "Usuario desconocido";
    const fullName = `${user.name ?? ""} ${user.lastname ?? ""}`.trim();
    return fullName || user.email || "Usuario desconocido";
  };

  // Rendered per event. `isLast` controls whether a connector line is drawn
  // below the dot — keyed off the full list so an event in the always-visible
  // block still connects down into the collapsible tail when it exists.
  const renderRow = (record: StatusHistoryRecord, absoluteIndex: number) => {
    const isLast = absoluteIndex === rows.length - 1;
    const comment = record.comments || record.description;

    return (
      <li key={record.id} className="flex gap-3">
        {/* Rail: dot + connector line down to the next entry */}
        <div className="flex flex-col items-center">
          <span
            className={cn(
              "mt-1 h-3 w-3 shrink-0 rounded-full",
              getStatusDotColor(record.new_status)
            )}
          />
          {!isLast && <span className="w-px flex-1 bg-gray-200" />}
        </div>

        {/* Content */}
        <div className={cn("min-w-0 flex-1", !isLast && "pb-5")}>
          <div className="flex items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {resolveStatusLabel(record)}
            </span>
            <time
              dateTime={record.changed_at}
              className="shrink-0 text-xs tabular-nums text-gray-400"
            >
              {formatDate(record.changed_at, DATE_TIME_OPTIONS)}
            </time>
          </div>
          <p className="mt-0.5 text-xs text-gray-500">
            Acción hecha por{" "}
            <span className="font-medium text-gray-700">
              {getUserDisplayName(record.changed_by)}
            </span>
          </p>
          {comment && (
            <p className="mt-2 whitespace-pre-wrap break-words rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">
              {comment}
            </p>
          )}
        </div>
      </li>
    );
  };

  if (loading) {
    return (
      <ol className="space-y-0">
        {[0, 1, 2].map((i) => (
          <li key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <Skeleton className="mt-1 h-3 w-3 shrink-0 rounded-full" />
              {i !== 2 && <span className="w-px flex-1 bg-gray-100" />}
            </div>
            <div className={cn("min-w-0 flex-1", i !== 2 && "pb-5")}>
              <div className="flex items-baseline justify-between gap-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-24" />
              </div>
              <Skeleton className="mt-1.5 h-3 w-40" />
            </div>
          </li>
        ))}
      </ol>
    );
  }

  if (!rows.length) {
    return (
      <div className="py-6 text-center text-sm text-gray-500">
        No hay historial de estados disponible
      </div>
    );
  }

  return (
    <div>
      <ol className="space-y-0">
        {alwaysRows.map((record, index) => renderRow(record, index))}
      </ol>

      {tailRows.length > 0 && (
        <div
          className={cn(
            "grid transition-[grid-template-rows] duration-300 ease-in-out",
            expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
          )}
        >
          <div className="overflow-hidden">
            <ol className="space-y-0">
              {tailRows.map((record, index) =>
                renderRow(record, VISIBLE_COUNT + index)
              )}
            </ol>
          </div>
        </div>
      )}

      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className="mt-3 flex items-center gap-1 pl-6 text-xs font-medium text-gray-500 transition-colors hover:text-gray-700"
        >
          <ChevronDown
            className={cn(
              "h-4 w-4 transition-transform duration-300",
              expanded && "rotate-180"
            )}
          />
          {expanded
            ? "Ver menos"
            : `Ver ${hiddenCount} ${hiddenCount === 1 ? "evento" : "eventos"} más`}
        </button>
      )}
    </div>
  );
};
